import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Camera, ArrowRight, X, Navigation, MapPin, ImagePlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import MapPicker from '@/components/MapPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Pushcart Vendors (Rehri/Patri)",
  "Fixed Footpath Vendors",
  "Mobile Hawkers (Walking Vendors)",
  "Cycle-Based Vendors",
  "Temporary Stall Vendors",
  "Truck/Van-Based Vendors",
  "Night Market Vendors",
  "Seasonal Vendors",
  "Stationary Kiosk-Type Vendors",
  "Hybrid Vendors (Cart + Fixed Spot)"
];

export default function NewChallanPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [lookupId, setLookupId] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    owner_name: '',
    fssai_code: '',
    mobile_number: '',
    business_type: '',
    latitude: 28.6139,
    longitude: 77.2090,
    amount: '',
    address: '',
    business_id_ref: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setForm(f => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        },
        () => {}
      );
    }
  }, []);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
  };

  const handleMapClick = (lat, lng) => {
    setForm(f => ({ ...f, latitude: lat, longitude: lng }));
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API}/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPhotos(prev => [...prev, { path: data.path, filename: data.filename }]);
      }
      toast.success('Photo uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    await uploadFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCameraCapture = async (e) => {
    await uploadFiles(e.target.files);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const handleBusinessLookup = async () => {
    if (!lookupId.trim()) return;
    setLookingUp(true);
    try {
      const { data } = await axios.get(`${API}/businesses/lookup/${lookupId.trim()}`, { withCredentials: true });
      setForm(f => ({
        ...f,
        business_name: data.business_name || f.business_name,
        owner_name: data.owner_name || f.owner_name,
        fssai_code: data.fssai_code || f.fssai_code,
        mobile_number: data.mobile_number || f.mobile_number,
        business_type: data.business_type || f.business_type,
        address: data.address || f.address,
        business_id_ref: data.business_id
      }));
      toast.success(`Business "${data.business_name}" found. Details auto-filled.`);
    } catch {
      toast.error('Business ID not found');
    } finally {
      setLookingUp(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.business_name.trim()) errs.business_name = 'Required';
    if (!form.owner_name.trim()) errs.owner_name = 'Required';
    if (!form.mobile_number.trim()) errs.mobile_number = 'Required';
    else if (!/^\d{10}$/.test(form.mobile_number.trim())) errs.mobile_number = 'Must be 10 digits';
    if (!form.business_type) errs.business_type = 'Required';
    if (!form.amount || parseFloat(form.amount) <= 0) errs.amount = 'Must be greater than 0';
    if (photos.length === 0) errs.photos = 'At least one photo is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        address: form.address.trim() || null,
        business_id_ref: form.business_id_ref || null,
        photo_paths: photos.map(p => p.path)
      };
      const { data } = await axios.post(`${API}/challans`, payload, { withCredentials: true });
      toast.success('Challan created successfully');
      navigate(`/challan/${data.id}/preview`);
    } catch (err) {
      toast.error('Failed to create challan');
    } finally {
      setSubmitting(false);
    }
  };

  const detectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setForm(f => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
          toast.success('Location detected');
        },
        () => toast.error('Could not detect location')
      );
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-4xl mx-auto animate-fade-in" data-testid="new-challan-page">
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-1" style={{ fontFamily: 'Outfit' }}>
        Issue New Challan
      </h1>
      <p className="text-sm sm:text-base text-slate-500 mb-6 sm:mb-8">Fill in the vendor details and offense information</p>

      <form onSubmit={handleSubmit} className="space-y-8 sm:space-y-10">
        {/* Business ID Lookup */}
        <section className="bg-blue-50/50 border border-blue-200 rounded-lg p-4 sm:p-5">
          <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-3" style={{ fontFamily: 'Outfit' }}>
            Returning Vendor? Enter Business ID
          </h2>
          <p className="text-xs sm:text-sm text-blue-600 mb-3">Enter a previously issued Business ID to auto-fill vendor details (photo still required)</p>
          <div className="flex gap-2">
            <Input
              data-testid="input-business-lookup"
              value={lookupId}
              onChange={(e) => setLookupId(e.target.value.toUpperCase())}
              placeholder="e.g., BIZ-A1B2C3D4"
              className="bg-white border-blue-200 focus:ring-2 focus:ring-blue-600 h-10 font-mono text-sm flex-1"
            />
            <Button
              type="button"
              onClick={handleBusinessLookup}
              disabled={lookingUp || !lookupId.trim()}
              data-testid="lookup-business-btn"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-100 h-10 px-4 font-semibold"
            >
              {lookingUp ? (
                <span className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
              ) : (
                <><Search className="w-4 h-4 mr-1.5" />Lookup</>
              )}
            </Button>
          </div>
          {form.business_id_ref && (
            <p className="text-xs text-blue-700 mt-2 font-mono">Linked: {form.business_id_ref}</p>
          )}
        </section>

        {/* Business Information */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-5 pb-2 border-b border-slate-200" style={{ fontFamily: 'Outfit' }}>
            Business Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            <div>
              <Label className="text-slate-700 font-medium text-sm">Business Name *</Label>
              <Input
                data-testid="input-business-name"
                value={form.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="e.g., Ram's Chaat Corner"
                className={`mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11 ${errors.business_name ? 'border-red-400' : ''}`}
              />
              {errors.business_name && <p className="text-red-500 text-xs mt-1">{errors.business_name}</p>}
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">Owner Name *</Label>
              <Input
                data-testid="input-owner-name"
                value={form.owner_name}
                onChange={(e) => handleChange('owner_name', e.target.value)}
                placeholder="Full name of the owner"
                className={`mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11 ${errors.owner_name ? 'border-red-400' : ''}`}
              />
              {errors.owner_name && <p className="text-red-500 text-xs mt-1">{errors.owner_name}</p>}
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">FSSAI Code (Optional)</Label>
              <Input
                data-testid="input-fssai"
                value={form.fssai_code}
                onChange={(e) => handleChange('fssai_code', e.target.value)}
                placeholder="14-digit FSSAI license number"
                className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11"
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-sm">Mobile Number *</Label>
              <Input
                data-testid="input-mobile"
                type="tel"
                inputMode="numeric"
                value={form.mobile_number}
                onChange={(e) => handleChange('mobile_number', e.target.value.replace(/\D/g, ''))}
                placeholder="10-digit mobile number"
                maxLength={10}
                className={`mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11 ${errors.mobile_number ? 'border-red-400' : ''}`}
              />
              {errors.mobile_number && <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700 font-medium text-sm">Business Type *</Label>
              <Select value={form.business_type} onValueChange={(v) => handleChange('business_type', v)}>
                <SelectTrigger data-testid="select-business-type" className={`mt-1.5 bg-slate-50 border-slate-200 h-11 ${errors.business_type ? 'border-red-400' : ''}`}>
                  <SelectValue placeholder="Select vendor type" />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.business_type && <p className="text-red-500 text-xs mt-1">{errors.business_type}</p>}
            </div>
            <div className="sm:col-span-2">
              <Label className="text-slate-700 font-medium text-sm">
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  Address Where Cart is Placed (Optional)
                </span>
              </Label>
              <Textarea
                data-testid="input-address"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="e.g., Near Gate No. 3, Connaught Place, New Delhi"
                rows={2}
                className="mt-1.5 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none text-sm"
              />
            </div>
          </div>
        </section>

        {/* Location */}
        <section>
          <div className="flex items-center justify-between mb-4 sm:mb-5 pb-2 border-b border-slate-200">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Offense Location
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={detectLocation}
              data-testid="detect-location-btn"
              className="text-blue-600 border-blue-200 hover:bg-blue-50 text-xs sm:text-sm"
            >
              <Navigation className="w-3.5 h-3.5 mr-1.5" />
              Auto-detect
            </Button>
          </div>
          
          <div className="rounded-lg overflow-hidden border border-slate-200 mb-4" style={{ height: '280px' }} data-testid="map-container">
            <MapPicker
              lat={form.latitude}
              lng={form.longitude}
              onLocationChange={handleMapClick}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label className="text-slate-700 font-medium text-xs sm:text-sm">Latitude</Label>
              <Input
                data-testid="input-latitude"
                value={form.latitude}
                className="mt-1 bg-slate-50 border-slate-200 h-9 sm:h-10 font-mono text-xs sm:text-sm"
                readOnly
              />
            </div>
            <div>
              <Label className="text-slate-700 font-medium text-xs sm:text-sm">Longitude</Label>
              <Input
                data-testid="input-longitude"
                value={form.longitude}
                className="mt-1 bg-slate-50 border-slate-200 h-9 sm:h-10 font-mono text-xs sm:text-sm"
                readOnly
              />
            </div>
          </div>
        </section>

        {/* Photo Upload */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-5 pb-2 border-b border-slate-200" style={{ fontFamily: 'Outfit' }}>
            Photo Evidence *
          </h2>
          
          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            className="hidden"
            data-testid="photo-file-input"
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraCapture}
            className="hidden"
            data-testid="camera-capture-input"
          />
          
          <div className="flex flex-wrap gap-3 sm:gap-4">
            {photos.map((photo, idx) => (
              <div key={idx} className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 group" data-testid={`photo-preview-${idx}`}>
                <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-slate-300 absolute inset-0 m-auto" />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-photo-${idx}`}
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] sm:text-[10px] px-1 py-0.5 truncate">
                  {photo.filename}
                </div>
              </div>
            ))}
            
            {/* Camera Capture Button */}
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="camera-capture-btn"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border-2 border-dashed border-blue-300 hover:border-blue-500 hover:bg-blue-50/50 flex flex-col items-center justify-center gap-1 transition-all duration-200 bg-blue-50/30"
            >
              {uploadingPhoto ? (
                <span className="w-5 h-5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
              ) : (
                <>
                  <Camera className="w-5 h-5 text-blue-500" />
                  <span className="text-[9px] sm:text-[10px] text-blue-500 font-semibold">Camera</span>
                </>
              )}
            </button>

            {/* File Upload Button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              data-testid="upload-photo-btn"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50/50 flex flex-col items-center justify-center gap-1 transition-all duration-200"
            >
              {uploadingPhoto ? (
                <span className="w-5 h-5 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
              ) : (
                <>
                  <ImagePlus className="w-5 h-5 text-slate-400" />
                  <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium">Gallery</span>
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Use Camera to click photos directly, or Gallery to upload from device</p>
          {errors.photos && <p className="text-red-500 text-xs mt-1 font-medium" data-testid="photo-error">{errors.photos}</p>}
        </section>

        {/* Amount */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4 sm:mb-5 pb-2 border-b border-slate-200" style={{ fontFamily: 'Outfit' }}>
            Penalty Amount
          </h2>
          <div className="max-w-xs">
            <Label className="text-slate-700 font-medium text-sm">Amount (INR) *</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
              <Input
                data-testid="input-amount"
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                value={form.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="500"
                className={`pl-8 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-600 focus:border-transparent h-11 text-lg font-semibold ${errors.amount ? 'border-red-400' : ''}`}
              />
            </div>
            {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
          </div>
        </section>

        {/* Submit */}
        <div className="pt-4 border-t border-slate-200 pb-8">
          <Button
            type="submit"
            disabled={submitting}
            data-testid="submit-challan-btn"
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold tracking-wide h-12 px-8 text-base transition-all duration-200"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Preview Challan
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
